import React, { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import questionPnc from "../../assets/questionPnc.svg";
import searchPnc from "../../assets/searchPnc.svg";
import "./UserMessage.css";

const UserMessage = ({ message, cycleId, id }) => {
  const { t, i18n } = useTranslation();
  
  if (!message) return null;

  if (message.messageId && message.messageId.includes("history")) {
    let vin = "";
    let mainGroup = "";
    let subGroup = "";
    let model = "";
    let classification = "";
    let queryText = "";
    let chassis1 = "";
    let chassis2 = "";

    // Extract VIN
    const vinMatch = message.text.match(/\*\*VIN\*\*:([^\n\\]+)/i);
    if (vinMatch && vinMatch[1]) {
      vin = vinMatch[1].trim();
    }

    // Extract Main Group
    const mainGroupMatch = message.text.match(/\*\*Main Group\*\*:([^\n\\]+)/i);
    if (mainGroupMatch && mainGroupMatch[1]) {
      mainGroup = mainGroupMatch[1].trim();
    }

    // Extract Sub Group
    const subGroupMatch = message.text.match(/\*\*Sub group\*\*:([^\n\\]+)/i);
    if (subGroupMatch && subGroupMatch[1]) {
      subGroup = subGroupMatch[1].trim();
    }

    // Extract Model if present
    const modelMatch = message.text.match(/\*\*Model\*\*:([^\n\\]+)/i);
    if (modelMatch && modelMatch[1]) {
      model = modelMatch[1].trim();
    }

    // Extract Classification if present
    const classMatch = message.text.match(/\*\*Classification\*\*:([^\n\\]+)/i);
    if (classMatch && classMatch[1]) {
      classification = classMatch[1].trim();
    }

    // Extract Chassis 1 if present
    const chassis1Match = message.text.match(/\*\*Chassis (?:No\. )?1\*\*:([^\n\\]+)/i);
    if (chassis1Match && chassis1Match[1]) {
      chassis1 = chassis1Match[1].trim();
    }

    // Extract Chassis 2 if present  
    const chassis2Match = message.text.match(/\*\*Chassis (?:No\. )?2\*\*:([^\n\\]+)/i);
    if (chassis2Match && chassis2Match[1]) {
      chassis2 = chassis2Match[1].trim();
    }

    // Extract the query part (last line)
    const lines = message.text.split("\\n");
    if (lines.length > 0) {
      queryText = lines[lines.length - 1].trim();
    }

    // Define color pairs for styling
    const colorPairs = [
      { labelBg: "bg-[#FFF1F1]", valueBg: "bg-[#FFFFFF]" },
      { labelBg: "bg-[#FFE2E2]", valueBg: "bg-[#EDEDED]" },
    ];

    // Create items to display with translated labels
    const itemsToDisplay = [];

    // Add VIN if present
    if (vin) {
      itemsToDisplay.push({ label: t('vin') || 'VIN', value: vin });
    }

    // Add Model and Classification if present
    if (model) {
      itemsToDisplay.push({ label: t('Model') || 'Model', value: model });
    }

    if (classification) {
      itemsToDisplay.push({ label: t('classification')?.replace('Enter ', '') || 'Classification', value: classification });
    }

    // Add Chassis 1 and Chassis 2 if present
    if (chassis1) {
      itemsToDisplay.push({ label: t('chassisNo1') || 'Chassis No. 1', value: chassis1 });
    }

    if (chassis2) {
      itemsToDisplay.push({ label: t('chassisNo2') || 'Chassis No. 2', value: chassis2 });
    }

    // Always add translated Main Group and Sub Group
    itemsToDisplay.push({ 
      label: t('mainGroup')?.replace('Select ', '') || 'Main Group', 
      value: mainGroup || t('noRelatedInfo') || 'No related information'
    });
    itemsToDisplay.push({ 
      label: t('subGroup')?.replace('Select ', '') || 'Sub Group', 
      value: subGroup || t('noRelatedInfo') || 'No related information'
    });

    return (
      <div
        className="bg-gray-100 rounded-lg ml-auto max-w-[500px] userPartPnc"
        id={message.messageId}
        data-message-id={message.messageId}
        data-message-text={queryText}
      >
        {/* Selections Section */}
        <div className="queryOptionsPnc">
          <div className="selectionsPnc">
            <div className="selectWithIcon">
              <img src={searchPnc} alt={t('findAnything') || 'Find anything'} /> {t('selectionSummary') || 'Selection Summary'}:
            </div>
          </div>
          <div className="inputPartPnc">
            <div className="inOptionPnc">
              {itemsToDisplay.map((item, index) => {
                const colorPair = colorPairs[index % 2];
                const isFirst = index === 0;
                const isLast = index === itemsToDisplay.length - 1;

                return (
                  <div className="vinOptionPnc" key={index}>
                    <div
                      className={`label vinLabelPnc ${
                        colorPair.labelBg
                      } px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""}`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`value ${colorPair.valueBg} px-2 py-1 ${
                        isFirst ? "rounded-tr-lg" : ""
                      }`}
                    >
                      {item.value || t('noRelatedInfo') || 'No related information'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Query Section */}
        <div className="questionPnc">
          <div className="questionIcoPnc">
            {/* <img src={questionPnc} alt={t('askQuestions')} /> Query */}
          </div>
          <div>
            <p className="questionPartPnc">{queryText}</p>
          </div>
        </div>
      </div>
    );
  }

  if (message.searchInfo) {
    const hasVin = message.searchInfo.vin && message.searchInfo.vin !== "N/A";
    const hasChassis1 = message.searchInfo.chassis1 && message.searchInfo.chassis1 !== "N/A";
    const hasChassis2 = message.searchInfo.chassis2 && message.searchInfo.chassis2 !== "N/A";
    
    const colorPairs = [
      { labelBg: "bg-[#FFF1F1]", valueBg: "bg-[#FFFFFF]" },
      { labelBg: "bg-[#FFE2E2]", valueBg: "bg-[#EDEDED]" },
    ];

    // Prepare items to display with translations
    const itemsToDisplay = [
      // Show VIN only if it has a valid value
      ...(hasVin ? [{ label: t('vin') || 'VIN', value: message.searchInfo.vin }] : []),

      // Show Model and Classification only if VIN doesn't have a valid value
      ...(!hasVin && !hasChassis1 && !hasChassis2
        ? [
            { label: t('Model') || 'Model', value: message.searchInfo.model },
            {
              label: t('classification')?.replace('Enter ', '') || 'Classification',
              value: message.searchInfo.classification,
            },
          ]
        : []),

      // Show Chassis 1 and Chassis 2 if they have valid values
      ...(hasChassis1 ? [{ label: t('chassisNo1') || 'Chassis No. 1', value: message.searchInfo.chassis1 }] : []),
      ...(hasChassis2 ? [{ label: t('chassisNo2') || 'Chassis No. 2', value: message.searchInfo.chassis2 }] : []),

      // Always show Main Group and Sub Group with translations
      { 
        label: t('mainGroup')?.replace('Select ', '') || 'Main Group', 
        value: message.searchInfo.mainGroup 
      },
      { 
        label: t('subGroup')?.replace('Select ', '') || 'Sub Group', 
        value: message.searchInfo.subGroup 
      },
    ];

    return (
      <div
        className="bg-gray-100 ml-auto max-w-[500px] userPartPnc custom-radius"
        id={message.messageId}
        data-message-id={message.messageId}
        data-message-text={message.text}
      >
        {/* Selections Section */}
        <div className="queryOptionsPnc">
          <div className="selectionsPnc">
            <div className="selectWithIcon">
              <img src={searchPnc} alt={t('findAnything') || 'Find anything'} /> {t('selectionSummary') || 'Selection Summary'}:
            </div>
          </div>
          <div className="inputPartPnc">
            <div className="inOptionPnc">
              {itemsToDisplay.map((item, index) => {
                // Get color pair based on index (alternating)
                const colorPair = colorPairs[index % 2];
                const isFirst = index === 0;
                const isLast = index === itemsToDisplay.length - 1;

                return (
                  <div className="vinOptionPnc" key={index}>
                    <div
                      className={`label vinLabelPnc ${
                        colorPair.labelBg
                      } px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""} ${
                        isLast ? "rounded-bl-lg" : ""
                      }`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`value ${colorPair.valueBg} px-2 py-1 ${
                        isFirst ? "rounded-tr-lg" : ""
                      } ${isLast ? "rounded-br-lg" : ""}`}
                    >
                      {item.value || t('noRelatedInfo') || 'No related information'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Query Section */}
        <div className="questionPnc">
          <div className="questionIcoPnc"></div>
          <div>
            <p className="questionPartPnc">{message.text}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-100 rounded-lg ml-auto max-w-[80%] userPartPnc"
      id={message.messageId}
      data-message-id={message.messageId}
      data-message-text={message.text}
    >
      <div className="questionPnc">
        <div className="questionIcoPnc"></div>
        <div>
          <p className="questionPartPnc">{message.text}</p>
        </div>
      </div>
    </div>
  );
};

export default UserMessage;